import React from "react";
import { ChevronRight } from "lucide-react";
import { BILLABLE_CALCULATION_LABELS, billableAllocationLabel, billingNumber, billingRateLabel, formatBillingDuration, formatBillingMoney, formatBillingNumber, formatBillingQuantity } from "../../utils/billingCalculation.js";
import { billablePrestationDetails, billableServiceContext, billingCustomerText } from "../../utils/billingPrestationDetails.js";
import BillingServiceRoute, { BillingLocations } from "./BillingServiceRoute.jsx";

export function useInvoiceReturnDetails() {
  const id = React.useId();
  const [expanded, setExpanded] = React.useState(new Set());
  return (line) => {
    const key = line.billable_item_id || line.id;
    return {
      id: `${id}-${key}`, details: line.return_details, line,
      label: line.order_reference || line.description || "la ligne", expanded: expanded.has(key),
      onToggle: () => setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      }),
    };
  };
}

export function InvoiceReturnToggle({ id, label, expanded, onToggle }) {
  return <button type="button" aria-label={`Détail de ${label}`} aria-expanded={expanded} aria-controls={id} onClick={onToggle}
    className={`ml-2 inline-flex h-6 items-center gap-0.5 rounded-[4px] border px-1 text-[11px] font-semibold focus-visible:outline-2 focus-visible:outline-accent ${expanded
      ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"}`}>
    <ChevronRight size={12} aria-hidden="true" className={expanded ? "rotate-90" : ""} />Détail
  </button>;
}

function LegacyReturnDetailsRow({ id, details, label, expanded, colSpan, currency = "CAD" }) {
  if (!details?.allocations?.length) return null;
  const base = details.base;
  const duration = (hours) => formatBillingDuration(Number(hours) * 3600);
  const included = [
    Number(base.inspection_time_hours) > 0 ? `Inspection : ${duration(base.inspection_time_hours)}` : null,
    base.tonne_rate != null ? `Supplément à la tonne : ${formatBillingNumber(base.tonnage)} t × ${billingRateLabel(base.tonne_rate, "tonne", currency)}` : null,
    base.voyage_amount != null ? `Montant fixe : ${formatBillingMoney(base.voyage_amount, currency)}` : null,
  ].filter(Boolean);
  const rows = [
    { ...base, label: `${base.kind === "loading" ? "Chargement" : "Livraison"} hors retour` },
    ...details.allocations.map((allocation) => ({ ...allocation, label: `Retour réparti · ${formatBillingNumber(Number(allocation.ratio) * 100, 2)} %` })),
  ];
  return <tr hidden={!expanded}>
    <td colSpan={colSpan} className="border-t border-slate-200 p-2">
      <section id={id} aria-label={`Détail de ${label}`} className="rounded-[4px] border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
        <table aria-label={`Composition de ${label}`} className="w-full text-left text-xs">
          <thead><tr><th className="px-2 py-1">Prestation</th><th className="px-2 py-1 text-right">Durée</th><th className="px-2 py-1 text-right">Tarif horaire</th><th className="px-2 py-1 text-right">Montant</th></tr></thead>
          <tbody>{rows.map((row, index) => <tr key={index}>
            <td className="px-2 py-1">{row.label}</td>
            <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{duration(row.time_hours)}</td>
            <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{billingRateLabel(row.unit_rate, "heure", currency)}</td>
            <td className="whitespace-nowrap px-2 py-1 text-right font-bold tabular-nums">{formatBillingMoney(row.line_total, currency)}</td>
          </tr>)}</tbody>
        </table>
        {included.length ? <p className="mt-1 px-2">Déjà inclus dans la prestation hors retour : {included.join(" · ")}.</p> : null}
        <p className="mt-1 px-2 text-[11px] text-slate-500">Retour réparti au prorata des durées avant inspection et retour, pour la même meunerie, le même relevé et la même date. Montants avant taxes.</p>
      </section>
    </td>
  </tr>;
}

function componentLabel(component) {
  if (component.kind === "manual_adjustment") return component.description || "Ajustement manuel";
  return BILLABLE_CALCULATION_LABELS[component.kind]
    || (component.billing_unit === "voyage" ? "Voyage" : "Prestation");
}

// The rounding footnote already covers the "≈" marker.
const plainDuration = (seconds) => formatBillingDuration(seconds).replace(/^≈ /, "");
const plainHours = (hours) => (billingNumber(hours) === null ? "—" : plainDuration(Number(hours) * 3600));

function sharedTimeKey(component) {
  const coefficient = component.calculation_details?.coefficient;
  return ["inspection", "return"].includes(component.kind) && billingNumber(coefficient?.denominator) > 0
    ? `${coefficient.numerator}/${coefficient.denominator}` : null;
}

// "23,58 % de 20 min (2 h 50 min / 12 h 1 min de temps direct) · 50 % pour cette commande"
function sharedTimeSummary(component, showBasis) {
  const source = component.calculation_details;
  const ratio = billingNumber(component.allocation?.ratio);
  const parts = [];
  if (sharedTimeKey(component)) {
    const numerator = Number(source.coefficient.numerator);
    const denominator = Number(source.coefficient.denominator);
    const base = source.source_seconds != null ? ` de ${plainDuration(source.source_seconds)}` : "";
    const basis = showBasis ? ` (${plainDuration(numerator)} / ${plainDuration(denominator)} de temps direct)` : "";
    parts.push(`${formatBillingNumber(numerator / denominator * 100, 2)} %${base}${basis}`);
  }
  if (ratio !== null && ratio < 1) parts.push(`${formatBillingNumber(ratio * 100, 2)} % pour cette commande`);
  return parts.join(" · ");
}

function SourceFacts({ component }) {
  const explanation = billablePrestationDetails(component);
  const context = billableServiceContext(component);
  const cargo = Array.isArray(component.service_context?.cargo_origins) ? component.service_context.cargo_origins : [];
  const coefficient = component.calculation_details?.coefficient;
  const loadingShare = ["loading", "preload"].includes(component.kind)
    && billingNumber(coefficient?.denominator) > 0;
  const allocation = loadingShare
    ? `${formatBillingNumber(coefficient.numerator)} / ${formatBillingNumber(coefficient.denominator)} de la base enregistrée : ${formatBillingNumber(Number(coefficient.numerator) / Number(coefficient.denominator) * 100, 2)} % de cette prestation.`
    : billableAllocationLabel(component);
  return <div className="grid gap-1.5 px-2 pb-2">
    {explanation.hourly ? <div className="flex flex-wrap gap-x-4 gap-y-1">
      {explanation.block ? <><span>Trajet : <strong>{formatBillingDuration(explanation.travel)}</strong></span><span>Sur place : <strong>{formatBillingDuration(explanation.onsite)}</strong></span></> : null}
      <span>{explanation.block ? "Bloc net" : "Durée avant répartition"} : <strong>{formatBillingDuration(explanation.net)}</strong></span>
      {explanation.excluded > 0 ? <span>Temps exclu : <strong>{formatBillingDuration(explanation.excluded)}</strong></span> : null}
    </div> : null}
    {["loading", "delivery"].includes(component.kind) || explanation.block ? <BillingServiceRoute context={context} /> : null}
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 text-xs text-slate-500">
      <span className="font-medium text-slate-600">Provenance de la marchandise</span>
      <div className="min-w-0 break-words">{cargo.length ? cargo.map((location, index) => <div key={index}>
        <BillingLocations locations={[{ name: billingCustomerText(location.name), address: billingCustomerText(location.address) }]} />
        {cargo.length > 1 && Array.isArray(location.order_references) ? <span>{location.order_references.map(billingCustomerText).filter(Boolean).join(" · ")}</span> : null}
      </div>) : "Information non disponible"}</div>
    </div>
    {allocation ? <p>{allocation} {explanation.hourly ? "Durée attribuée" : "Quantité attribuée"} : <strong title={String(component.quantity ?? "")}>{formatBillingQuantity(component.quantity, component.billing_unit)}</strong>.</p> : null}
    {!component.calculation_details ? <p className="text-slate-500">Le détail du parcours n’est pas disponible pour cette prestation.</p> : null}
  </div>;
}

// A fixed amount has no quantity to multiply: say where it comes from, and the stop share if any.
const fixedFeeBasis = source => [source?.fixed_fee_source === "aspiration" ? "Aspiration" : "Montant fixe",
  billingNumber(source?.coefficient?.denominator) > 0
    ? `${formatBillingNumber(source.coefficient.numerator)} t sur ${formatBillingNumber(source.coefficient.denominator)} t de l’arrêt` : "forfait"].join(" · ");

function ComponentCalculation({ component, currency, showBasis }) {
  const source = component.calculation_details;
  const summary = sharedTimeSummary(component, showBasis);
  return <>
    <div className="flex flex-wrap justify-end gap-x-1 tabular-nums">
      {component.kind === "manual_adjustment" ? <span>Montant saisi</span> : component.kind === "fixed_fee" ? <span>{fixedFeeBasis(source)}</span> : <>
        <span title={String(component.quantity ?? "")}>{component.billing_unit === "heure" ? plainHours(component.quantity) : formatBillingQuantity(component.quantity, component.billing_unit)}</span>
        <span>×</span><span>{billingRateLabel(component.unit_rate, component.billing_unit, currency)}</span>
      </>}
    </div>
    {summary ? <p className="mt-0.5 text-[11px] text-slate-500">{summary}</p> : null}
    {source?.exceptional_rate ? <p className="mt-0.5 text-[11px] text-amber-900">Tarif exceptionnel de la commande.</p> : null}
    {billingNumber(component.rounding_difference) ? <p className="mt-0.5 text-[11px] text-slate-500">Répartition des centimes : {Number(component.rounding_difference) > 0 ? "+" : ""}{formatBillingMoney(component.rounding_difference, currency)} par rapport au produit arrondi.</p> : null}
  </>;
}

export function InvoiceReturnDetailsRow({ line, ...props }) {
  const { id, label, expanded, colSpan, currency = "CAD" } = props;
  const components = line?.calculation_details?.components;
  if (!components?.length && props.details?.allocations?.length) return <LegacyReturnDetailsRow {...props} />;
  const rows = components?.length ? components : [{ ...line, kind: line?.is_manual ? "manual_adjustment" : null }];
  const primary = rows.find((component) => component.calculation_details?.block)
    || rows.find((component) => !component.allocation) || rows[0];
  const manual = line?.is_manual;
  // Explain the direct-time basis once per shared-time share, not on every row.
  const seenBases = new Set();
  const showBasis = rows.map((component) => {
    const key = sharedTimeKey(component);
    if (!key || seenBases.has(key)) return false;
    seenBases.add(key);
    return true;
  });
  const shared = rows.some((component) => component.allocation || sharedTimeKey(component));
  return <tr hidden={!expanded}>
    <td colSpan={colSpan} className="border-t border-slate-200 p-2">
      <section id={id} aria-label={`Détail de ${label}`} className="rounded-[4px] border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
        {!manual ? <SourceFacts component={primary} /> : null}
        <table aria-label={`Composition de ${label}`} className="w-full text-left text-xs">
          <thead><tr><th scope="col" className="px-2 py-1">Prestation</th><th scope="col" className="px-2 py-1 text-right">Calcul</th><th scope="col" className="px-2 py-1 text-right">Montant</th></tr></thead>
          <tbody>{rows.map((component, index) => <tr key={`${component.invoice_line_id || "component"}-${index}`}>
            <td className="whitespace-nowrap px-2 py-1 align-top">{componentLabel(component)}</td>
            <td className="px-2 py-1 text-right"><ComponentCalculation component={component} currency={currency} showBasis={showBasis[index]} /></td>
            <td className="whitespace-nowrap px-2 py-1 text-right align-top font-bold tabular-nums">{formatBillingMoney(component.line_total, currency)}</td>
          </tr>)}</tbody>
          <tfoot><tr className="border-t border-slate-200"><th scope="row" colSpan={2} className="px-2 py-1 text-right">Total de la ligne avant taxes</th><td className="whitespace-nowrap px-2 py-1 text-right font-bold tabular-nums">{formatBillingMoney(line.line_total, currency)}</td></tr></tfoot>
        </table>
        {shared || !manual ? <p className="mt-1 px-2 text-[11px] text-slate-500"
          title={shared ? "Même relevé, même entreprise facturée et même date. Le retour utilise les durées avant inspection et retour. Les montants enregistrés utilisent les valeurs exactes." : "Les montants enregistrés utilisent les valeurs exactes."}>
          {shared ? "Temps communs répartis au prorata du temps direct. " : ""}{!manual ? "Durées arrondies à la seconde." : ""}
        </p> : null}
      </section>
    </td>
  </tr>;
}
